import React, {useCallback, useContext, useEffect, useState} from 'react';

import {SmartSection} from 'src/components/smart-section';
import {WidthItem} from 'src/components/width-item';
import {WrappedEllipsisText} from 'src/components/wrapped-ellipsis-text';
import {SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';

import Button, {FlatButton} from '@pb/kit-component-button';
import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';
import {Textarea} from '@pb/kit-component-textarea-v2';

import {NotesSectionSkeleton} from './notes-section-skeleton';

import type {MessageContextType} from 'src/contexts/message-context';
import type {Error} from 'src/openapi/admin/models/Error';


type Props = {
    disabled?: boolean,
    isLoading: boolean,
    headline: string,
    canEdit: boolean,
    loadingError: Error | null,
    isNotesChanged: boolean,

    notes: string,
    chunk: string,

    onChange: (notes: {value: string}) => void,
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    onSave: () => Promise<string | void | undefined>,
    onDiscard: () => void,
};

export const NotesSection = (props: Props) => {
    const {
        disabled,
        isLoading,
        headline,
        loadingError,
        isNotesChanged,

        canEdit,
        notes,
        chunk,

        onChange,
        onSave,
        onDiscard,
    } = props;

    const [editMode, setEditMode] = useState(false);
    const [saveAccountNotesBusy, setSaveAccountNotesBusy] = useState(false);

    const {setSuccessMessage, setErrorMessage} = useContext<MessageContextType>(MessageContext);
    const {isCurrentChunkChanged, setIsChunkChanged, proceed} = useConfirmationChunk({
        chunkName: chunk,
        onProceed: () => setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE),
    });

    useEffect(() => setIsChunkChanged(isNotesChanged), [isNotesChanged, setIsChunkChanged]);

    const onEditNotes = useCallback(() => {
        setEditMode(true);
    }, []);

    const saveNotes = useCallback(async () => {
        setSaveAccountNotesBusy(true);
        const error = await onSave();
        setSaveAccountNotesBusy(false);

        if (error) {
            setErrorMessage(error);
        } else {
            proceed();
        }

        setEditMode(false);
    }, [onSave, proceed, setErrorMessage]);

    const discardNotes = useCallback(() => {
        onDiscard();
        setEditMode(false);
    }, [onDiscard]);

    return (
        <SmartSection
            isLoading={isLoading}
            hasLoadingError={loadingError}
            skeletonView={<NotesSectionSkeleton/>}>
            <FormSection
                headline={headline}
                info={headlineNode => (
                    <Item grow={1}>
                        <Group justify="space-between">
                            {headlineNode}
                            {canEdit ? (
                                <FlatButton disabled={disabled} onClick={onEditNotes}>
                                    <b>Edit</b>
                                </FlatButton>
                            ) : null}
                        </Group>
                    </Item>
                )}>
                {editMode ? (
                    <Group direction="v">
                        <WidthItem width="100%">
                            <Textarea
                                value={notes}
                                onChange={e => onChange({value: e.target.value})}
                            />
                        </WidthItem>
                        <Group>
                            <Button
                                busy={saveAccountNotesBusy}
                                disabled={disabled || !isCurrentChunkChanged}
                                variant="Type.PRIMARY"
                                onClick={saveNotes}>
                                Save
                            </Button>
                            <Button variant="Type.SECONDARY" onClick={discardNotes}>
                                Cancel
                            </Button>
                        </Group>
                    </Group>
                ) : <WrappedEllipsisText text={notes}/>}
            </FormSection>
        </SmartSection>
    );
};
